import { ArrowLeft, Save } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../api/api";
import "./AdminEdit.css";
import LoadingSpinner from "./LoadingSpinner";

const AdminSessionsEdit = () => {
  const navigate = useNavigate();
  const { locationSlug } = useParams();
  
  const [sessionInfo, setSessionInfo] = useState(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const data = await api.getSessionInfo(locationSlug);
        if (mounted) setSessionInfo(data);
      } catch (err) {
        console.error("Failed to load session info:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [locationSlug]);

  const handleSave = async () => {
    try {
      await api.updateSessionInfo(locationSlug, sessionInfo);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save session info:", err);
      alert("Erreur lors de la sauvegarde. Voir la console.");
    }
  };

  if (loading || !sessionInfo) {
    return <LoadingSpinner />;
  }

  return (
    <div className="admin-edit-page">
      <div className="admin-header">
        <div className="container">
          <button
            className="back-btn"
            onClick={() =>
              navigate("/admin/dashboard", {
                state: { selectedLocation: locationSlug },
              })
            }
          >
            <ArrowLeft size={20} /> Retour au dashboard
          </button>
          <div className="admin-header-content">
            <h1 className="heading-md">Modifier - Informations Séances</h1>
            <button
              className="btn-primary"
              onClick={handleSave}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <Save size={18} /> {saved ? "✓ Sauvegardé" : "Sauvegarder"}
            </button>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="edit-form">
            {/* What is section */}
            <div className="form-section">
              <h2 className="heading-sm">Qu'est-ce qu'une séance ?</h2>
              
              <div className="form-group">
                <label htmlFor="what_is_title">Titre</label>
                <input
                  type="text"
                  id="what_is_title"
                  value={sessionInfo.what_is_title}
                  onChange={(e) =>
                    setSessionInfo({
                      ...sessionInfo,
                      what_is_title: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="what_is_text">Contenu</label>
                <textarea
                  id="what_is_text"
                  value={sessionInfo.what_is_text}
                  onChange={(e) =>
                    setSessionInfo({
                      ...sessionInfo,
                      what_is_text: e.target.value,
                    })
                  }
                  className="form-textarea"
                  rows="5"
                  placeholder="Décrivez ce qu'est une séance de kinésithérapie..."
                />
              </div>
            </div>

            {/* When to consult section */}
            <div className="form-section">
              <h2 className="heading-sm">Quand consulter ?</h2>
              
              <div className="form-group">
                <label htmlFor="when_consult_title">Titre</label>
                <input
                  type="text"
                  id="when_consult_title"
                  value={sessionInfo.when_consult_title}
                  onChange={(e) =>
                    setSessionInfo({
                      ...sessionInfo,
                      when_consult_title: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="when_consult_text">Contenu</label>
                <textarea
                  id="when_consult_text"
                  value={sessionInfo.when_consult_text}
                  onChange={(e) =>
                    setSessionInfo({
                      ...sessionInfo,
                      when_consult_text: e.target.value,
                    })
                  }
                  className="form-textarea"
                  rows="5"
                  placeholder="Expliquez dans quels cas consulter un kinésithérapeute..."
                />
              </div>
            </div>

            {/* Session process section */}
            <div className="form-section">
              <h2 className="heading-sm">Déroulement d'une séance</h2>
              
              <div className="form-group">
                <label htmlFor="session_process_title">Titre</label>
                <input
                  type="text"
                  id="session_process_title"
                  value={sessionInfo.session_process_title}
                  onChange={(e) =>
                    setSessionInfo({
                      ...sessionInfo,
                      session_process_title: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="session_process_text">Contenu</label>
                <textarea
                  id="session_process_text"
                  value={sessionInfo.session_process_text}
                  onChange={(e) =>
                    setSessionInfo({
                      ...sessionInfo,
                      session_process_text: e.target.value,
                    })
                  }
                  className="form-textarea"
                  rows="5"
                  placeholder="Décrivez comment se déroule une séance typique..."
                />
              </div>
            </div>

            {/* Required documents section */}
            <div className="form-section">
              <h2 className="heading-sm">Documents nécessaires</h2>
              
              <div className="form-group">
                <label htmlFor="required_docs_title">Titre</label>
                <input
                  type="text"
                  id="required_docs_title"
                  value={sessionInfo.required_docs_title}
                  onChange={(e) =>
                    setSessionInfo({
                      ...sessionInfo,
                      required_docs_title: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="required_docs_text">Contenu</label>
                <textarea
                  id="required_docs_text"
                  value={sessionInfo.required_docs_text}
                  onChange={(e) =>
                    setSessionInfo({
                      ...sessionInfo,
                      required_docs_text: e.target.value,
                    })
                  }
                  className="form-textarea"
                  rows="5"
                  placeholder="Listez les documents à apporter (ordonnance, carte vitale, etc.)..."
                />
              </div>
            </div>

            {/* Refund section */}
            <div className="form-section">
              <h2 className="heading-sm">Remboursement</h2>
              
              <div className="form-group">
                <label htmlFor="refund_title">Titre</label>
                <input
                  type="text"
                  id="refund_title"
                  value={sessionInfo.refund_title}
                  onChange={(e) =>
                    setSessionInfo({
                      ...sessionInfo,
                      refund_title: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="refund_text">Contenu</label>
                <textarea
                  id="refund_text"
                  value={sessionInfo.refund_text}
                  onChange={(e) =>
                    setSessionInfo({
                      ...sessionInfo,
                      refund_text: e.target.value,
                    })
                  }
                  className="form-textarea"
                  rows="5"
                  placeholder="Expliquez les modalités de remboursement..."
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminSessionsEdit;
